import { useToast } from '@driftt/dds'
import axios, { AxiosResponse } from 'axios'
import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { ConversationAPI } from 'api/index'
import { PROMPT_MODES } from 'components/AIConfiguration/LeadQualification/constants'
import {
  PersonaPayload,
  PersonaResponse,
  QualificationSettings,
} from 'components/AIConfiguration/LeadQualification/types'
import { Attribute } from 'components/AIConfiguration/LeadQualification/types'

export const ATTRIBUTES_QUERY_KEY = 'qualification'

const DEFAULT_SETTINGS = {
  questionConfig: {
    isAI: true,
  },
  scoringConfig: {
    isAI: true,
  },
}

const getAttributeSettings = async (url: string) => {
  const res = await ConversationAPI.get<PersonaResponse[]>(url)

  return {
    ...DEFAULT_SETTINGS,
    qualificationPersonas: (res?.data || ([] as PersonaResponse[])).map(
      ({ prompt, attributes, ...data }) => ({
        ...data,
        prompt,
        attributes,
        mode:
          !prompt && attributes.length > 0
            ? PROMPT_MODES.MANUAL
            : PROMPT_MODES.AI,
      })
    ),
  }
}

const deleteQualificationPersona = async (qualificationPersonaId: number) =>
  await ConversationAPI.delete(`qualification/${qualificationPersonaId}`)

const updateQualificationPersona = async (payload: PersonaPayload) =>
  !!payload.id
    ? await ConversationAPI.put<PersonaResponse>(
        `qualification/${payload.id}`,
        payload
      )
    : await ConversationAPI.post<PersonaResponse>(`qualification`, payload)

export const generateAttributes = async (prompt: string) => {
  const res = await ConversationAPI.post<
    { prompt: string },
    AxiosResponse<Attribute[]>
  >('qualification/attributes/find', { prompt })
  return res.data
}

export const generatePrompt = async (attributes: Attribute[]) => {
  const res = await ConversationAPI.post<Attribute[], AxiosResponse<string>>(
    'qualification/prompt/find',
    {
      attributes,
    }
  )

  return res.data
}

export const useGPTQualificationAttributes = (config?: {
  onSavePersonaSuccess?: () => void
  shouldFetchAttributes?: boolean
}) => {
  const { onSavePersonaSuccess } = config || {}
  const queryClient = useQueryClient()
  const toast = useToast()
  const {
    isError,
    error,
    data: attributeSettings,
  } = useQuery({
    queryKey: [ATTRIBUTES_QUERY_KEY],
    queryFn: ({ queryKey }) => getAttributeSettings(queryKey[0]),
    refetchOnWindowFocus: false,
    enabled: !!config?.shouldFetchAttributes,
  })

  useEffect(() => {
    if (isError && axios.isAxiosError(error) && error.message.includes('404'))
      toast({
        message: 'There was an issue fetching qualification attributes',
        variant: 'error',
      })
  }, [error, isError, toast])

  const handleDeletePersona = useMutation(deleteQualificationPersona, {
    onSuccess: () => {
      queryClient.invalidateQueries(ATTRIBUTES_QUERY_KEY)
      toast({
        message: 'Deleted successfully!',
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        message: 'An error ocurred on deletion!',
        variant: 'error',
      })
    },
  })

  const handleSavePersona = useMutation({
    mutationFn: updateQualificationPersona,
    onMutate: async (newAttributes) => {
      await queryClient.cancelQueries({ queryKey: ATTRIBUTES_QUERY_KEY })

      const previousSettings = queryClient.getQueryData(ATTRIBUTES_QUERY_KEY)

      queryClient.setQueryData(ATTRIBUTES_QUERY_KEY, (prev: unknown) => {
        const old = prev as QualificationSettings
        return prev
          ? {
              ...old,
              attributeConfig: {
                id: newAttributes.id,
                prompt: newAttributes.prompt,
                attributes: newAttributes.attributes,
                mode:
                  newAttributes.prompt === null &&
                  newAttributes.attributes.length > 0
                    ? PROMPT_MODES.MANUAL
                    : PROMPT_MODES.AI,
              },
            }
          : old
      })

      return { previousSettings }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(ATTRIBUTES_QUERY_KEY)
      toast({
        message: 'Qualification rules saved successfully',
        variant: 'success',
      })
      if (onSavePersonaSuccess) onSavePersonaSuccess()
    },
    onError: (_err, _updatedCriteria, context) => {
      queryClient.setQueryData(ATTRIBUTES_QUERY_KEY, context?.previousSettings)
      toast({
        message:
          "Oops! Something went wrong. \nOur team has been notified, and we're working to fix it",
        variant: 'error',
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ATTRIBUTES_QUERY_KEY })
    },
  })

  const generateAIAttributes = useMutation({
    mutationFn: (prompt: string) => generateAttributes(prompt),
    onSuccess: (data: Attribute[]) => {
      if (data.length === 0)
        toast({
          message: 'We could not find any attribute. Please try again.',
          variant: 'error',
        })
    },
    onError: (error) => {
      toast({
        message:
          'There was an error calculating the attributes. Please try again later.',
        variant: 'error',
      })
    },
  })

  const generateAIPrompt = useMutation({
    mutationFn: (attributes: Attribute[]) => generatePrompt(attributes),
    onError: () => {
      toast({
        message: 'There was an error generating the prompt.',
        variant: 'error',
      })
    },
  })

  return {
    qualificationPersona: attributeSettings?.qualificationPersonas?.[0],
    handleDeletePersona,
    handleSavePersona,
    generateAIAttributes,
    generateAIPrompt,
  }
}
